import { NormalizedNumber } from '@sparkdotfi/common-universal'
import { TimerResetIcon } from 'lucide-react'
import { ReactNode } from 'react'

import { Token } from '@/domain/types/Token'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/ui/atoms/tooltip/Tooltip'
import { testIds } from '@/ui/utils/testIds'
import { useTimestamp } from '@/utils/useTimestamp'

interface CooldownTimerProps {
  renewalPeriod: number
  latestUpdateTimestamp: number
  token: Token
  gap: NormalizedNumber
  cooldownOverContent?: ReactNode
  cooldownActiveContent?: ReactNode
  /**
   * @warning This prop is only for storybook purposes.
   */
  forceOpen?: boolean
}

export function CooldownTimer(props: CooldownTimerProps) {
  const { timestamp } = useTimestamp({
    refreshIntervalInMs: 1000,
  })
  const { renewalPeriod, latestUpdateTimestamp, forceOpen, gap, token } = props
  const timeLeft = Math.max(0, renewalPeriod - (timestamp - latestUpdateTimestamp))
  const cooldownOverContent = props.cooldownOverContent ?? (
    <>
      The cap renewal cooldown is over. <br /> It might be changed at any time.
    </>
  )
  const cooldownActiveContent = props.cooldownActiveContent ?? (
    <>
      The instantly available cap has <br /> a renewal time of {secondsToHours(props.renewalPeriod)} hours.
    </>
  )

  return (
    <Tooltip open={forceOpen}>
      <TooltipTrigger data-testid={testIds.marketDetails.capAutomator.cooldownTimer}>
        <TimerResetIcon size={16} className="text-fg-brand-tertiary" />
      </TooltipTrigger>
      <TooltipContent className="p-3">
        <div className="typography-label-4 flex flex-col gap-3 text-tertiary">
          {timeLeft > 0 && (
            <div className="flex flex-col gap-1">
              <span>Cooldown period:</span>
              <span className="typography-label-2 text-primary-inverse">{secondsToTime(timeLeft)}</span>
            </div>
          )}
          <div>{timeLeft === 0 ? cooldownOverContent : cooldownActiveContent}</div>
          <div className="flex flex-col">
            <span>Cap Automator increase:</span>
            <span className="text-primary-inverse">
              {token.format(gap, { style: 'compact' })} {token.symbol}
            </span>
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  )
}

function secondsToHours(seconds: number) {
  return Math.floor(seconds / 3600)
}

function secondsToTime(seconds: number) {
  const hours = secondsToHours(seconds)
  const minutes = Math.floor((seconds % 3600) / 60)
  const sec = seconds % 60

  function formatTime(num: number) {
    return num.toString().padStart(2, '0')
  }

  return `${hours}h ${formatTime(minutes)}m ${formatTime(sec)}s`
}
